import React, { useState, useEffect, useCallback } from 'react';
import { getSellerManagementStatusHistory, setSellerManagedStatus, assignSellerOwner } from '../../../../services/AdminApis';
import { getAllAdmins } from '../../../../services/couponAdminApi';
import { useToast } from '../../../../hooks/use-toast';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../ui/accordion';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Switch } from '../../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';

const SOURCE_LABELS = {
    ADMIN_MANUAL_OVERRIDE: 'Manual',
    ADMIN_UI_OWNER_ASSIGNMENT: 'Owner Reassigned',
};

const formatToIST = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: 'numeric',
        month: 'short',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const SellerManagementStatusHistory = ({ sellerProfileId, isManagedSeller, onStatusChange }) => {
    const { toast } = useToast();
    const { hasPermission } = usePermissions();
    const canSetManagedStatus = hasPermission('sales:manages-seller-accounts');
    const canAssignOwner = hasPermission('sales:manages-seller-assignment');
    const canViewAny = canSetManagedStatus || canAssignOwner;

    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [overrideTarget, setOverrideTarget] = useState(!!isManagedSeller);
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [admins, setAdmins] = useState([]);
    const [selectedOwnerId, setSelectedOwnerId] = useState('');
    const [isAssigningOwner, setIsAssigningOwner] = useState(false);

    useEffect(() => {
        setOverrideTarget(!!isManagedSeller);
    }, [isManagedSeller]);

    useEffect(() => {
        if (!canAssignOwner) return;
        getAllAdmins()
            .then((res) => setAdmins(res.data || []))
            .catch((err) => console.error('Failed to fetch admin list for owner assignment:', err));
    }, [canAssignOwner]);

    const fetchHistory = useCallback(async () => {
        if (!canViewAny) return;
        setIsLoading(true);
        try {
            const data = await getSellerManagementStatusHistory(sellerProfileId);
            setHistory(data || []);
            setError('');
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to fetch managed-status history.';
            setError(errorMsg);
            toast({ variant: 'destructive', title: 'Error', description: errorMsg });
        } finally {
            setIsLoading(false);
        }
    }, [sellerProfileId, toast, canViewAny]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const handleOverrideSubmit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            toast({ variant: 'destructive', title: 'Reason Required', description: 'Please provide a reason.' });
            return;
        }

        setIsSubmitting(true);
        try {
            const updatedInfo = await setSellerManagedStatus(sellerProfileId, overrideTarget, reason.trim());
            toast({ title: 'Success', description: `Seller marked as ${overrideTarget ? 'Managed' : 'Unmanaged'}.` });
            setReason('');
            if (onStatusChange) onStatusChange(updatedInfo);
            fetchHistory();
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to update managed status.';
            toast({ variant: 'destructive', title: 'Override Failed', description: errorMsg });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAssignOwner = async (e) => {
        e.preventDefault();
        if (!selectedOwnerId) {
            toast({ variant: 'destructive', title: 'Admin Required', description: 'Please select an admin.' });
            return;
        }

        setIsAssigningOwner(true);
        try {
            const updatedInfo = await assignSellerOwner(sellerProfileId, Number(selectedOwnerId));
            toast({ title: 'Success', description: 'Zoho Account owner reassigned.' });
            if (onStatusChange) onStatusChange(updatedInfo);
            fetchHistory();
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to assign owner.';
            toast({ variant: 'destructive', title: 'Assignment Failed', description: errorMsg });
        } finally {
            setIsAssigningOwner(false);
        }
    };

    if (!canViewAny) return null;

    const columnCount = 2 + (canAssignOwner ? 1 : 0) + (canSetManagedStatus ? 1 : 0);

    return (
        <Card className="border-none shadow-none">
            <CardHeader className="px-0 pb-2">
                <CardTitle className="text-base">Managed Seller Status</CardTitle>
            </CardHeader>
            <CardContent className="px-0 space-y-3">
                <Accordion type="single" collapsible className="w-full border rounded-lg">
                    {canAssignOwner && (
                        <AccordionItem value="assign-owner" className={`px-3 ${canSetManagedStatus ? '' : 'border-b-0'}`}>
                            <AccordionTrigger className="text-sm py-2.5">Assign Zoho Account Owner</AccordionTrigger>
                            <AccordionContent className="pb-3">
                                <form onSubmit={handleAssignOwner} className="space-y-3">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="assign-owner-select" className="text-xs">New Owner</Label>
                                        <Select value={selectedOwnerId} onValueChange={setSelectedOwnerId} disabled={isAssigningOwner}>
                                            <SelectTrigger id="assign-owner-select" className="h-9 text-sm">
                                                <SelectValue placeholder="Select an admin..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {admins.length > 0 ? (
                                                    admins.map((a) => (
                                                        <SelectItem key={a.id} value={String(a.id)}>
                                                            {a.email} ({a.group || 'No Group'})
                                                        </SelectItem>
                                                    ))
                                                ) : (
                                                    <SelectItem value="no-admins" disabled>No admins found</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">Admin must have a Zoho CRM user ID configured.</p>
                                    </div>
                                    <Button type="submit" size="sm" disabled={isAssigningOwner}>
                                        {isAssigningOwner ? 'Assigning...' : 'Assign Owner'}
                                    </Button>
                                </form>
                            </AccordionContent>
                        </AccordionItem>
                    )}

                    {canSetManagedStatus && (
                        <AccordionItem value="set-managed" className="px-3 border-b-0">
                            <AccordionTrigger className="text-sm py-2.5">Set Managed Status</AccordionTrigger>
                            <AccordionContent className="pb-3">
                                <form onSubmit={handleOverrideSubmit} className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            id="managed-override-switch"
                                            checked={overrideTarget}
                                            onCheckedChange={setOverrideTarget}
                                            disabled={isSubmitting}
                                        />
                                        <Label htmlFor="managed-override-switch" className="text-sm">
                                            Mark as {overrideTarget ? 'Managed' : 'Unmanaged'}
                                        </Label>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="managed-override-reason" className="text-xs">Reason</Label>
                                        <Textarea
                                            id="managed-override-reason"
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder="Why is this changing?"
                                            disabled={isSubmitting}
                                            rows={2}
                                            className="text-sm"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Activating requires an admin already assigned as owner. Zoho ownership does not affect this flag.
                                        </p>
                                    </div>
                                    <Button type="submit" size="sm" disabled={isSubmitting}>
                                        {isSubmitting ? 'Saving...' : 'Save'}
                                    </Button>
                                </form>
                            </AccordionContent>
                        </AccordionItem>
                    )}
                </Accordion>

                {error && <Alert variant="destructive"><AlertDescription className="text-sm">{error}</AlertDescription></Alert>}

                <div className="overflow-x-auto border rounded-lg">
                    <Table className="text-xs">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-xs whitespace-nowrap">Date</TableHead>
                                {canAssignOwner && <TableHead className="text-xs">Owner Change</TableHead>}
                                {canSetManagedStatus && <TableHead className="text-xs">Status Change</TableHead>}
                                <TableHead className="text-xs">Details</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columnCount} className="text-center text-muted-foreground py-4">
                                        Loading...
                                    </TableCell>
                                </TableRow>
                            ) : history.length > 0 ? (
                                history.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell className="whitespace-nowrap">{formatToIST(row.created_at)}</TableCell>
                                        {canAssignOwner && (
                                            <TableCell className="text-muted-foreground">
                                                {row.old_owner_admin_email || 'N/A'} &rarr; {row.new_owner_admin_email || 'N/A'}
                                            </TableCell>
                                        )}
                                        {canSetManagedStatus && (
                                            <TableCell>
                                                <Badge variant="outline" className="text-xs whitespace-nowrap">
                                                    {row.old_is_managed_seller ? 'Managed' : 'Unmanaged'} &rarr; {row.new_is_managed_seller ? 'Managed' : 'Unmanaged'}
                                                </Badge>
                                            </TableCell>
                                        )}
                                        <TableCell
                                            className="max-w-[160px] truncate text-muted-foreground"
                                            title={[row.triggered_by_admin_email, row.reason].filter(Boolean).join(' — ')}
                                        >
                                            {SOURCE_LABELS[row.source] || row.source}
                                            {row.triggered_by_admin_email ? ` (${row.triggered_by_admin_email})` : ''}
                                            {row.reason ? ` - ${row.reason}` : ''}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columnCount} className="text-center text-muted-foreground py-4">
                                        No changes recorded.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default SellerManagementStatusHistory;
